import { useMemo, useState } from 'react';
import type { Appointment } from '@fitplatform/shared';
import { Button, Muted } from '@/components/ui';
import { hasStarted } from '@/lib/appointmentTime';

/**
 * Month calendar of the client's scheduled sessions. Days with one or more
 * upcoming sessions are marked; tapping a day lists that day's sessions with
 * reschedule / cancel actions. Read-and-manage only — booking lives in the
 * Book tab. Mirrors the dark luxury palette.
 */

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function ymd(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function label12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}
function fullDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export function SessionsCalendar({
  appointments,
  onReschedule,
  onCancel,
  cancelPending,
}: {
  appointments: Appointment[];
  onReschedule: (a: Appointment) => void;
  onCancel: (a: Appointment) => void;
  cancelPending: boolean;
}) {
  const today = todayLocal();
  const [cursor, setCursor] = useState(() => {
    const [y, m] = today.split('-').map(Number);
    return { year: y, month: m - 1 }; // month 0-indexed
  });
  const [selected, setSelected] = useState<string | null>(today);

  // date -> sessions on that date (upcoming only), sorted by start.
  const byDate = useMemo(() => {
    const map: Record<string, Appointment[]> = {};
    for (const a of appointments) {
      if (a.status !== 'upcoming') continue;
      (map[a.date] ??= []).push(a);
    }
    for (const k of Object.keys(map)) map[k].sort((x, y) => x.start_time.localeCompare(y.start_time));
    return map;
  }, [appointments]);

  const { year, month } = cursor;
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthLabel = new Date(year, month, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  // Leading blanks + day cells.
  const cells: Array<string | null> = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(ymd(year, month, d));

  function shiftMonth(delta: number) {
    setCursor((c) => {
      const m = c.month + delta;
      const year = c.year + Math.floor(m / 12);
      const month = ((m % 12) + 12) % 12;
      return { year, month };
    });
  }

  const selectedSessions = selected ? byDate[selected] ?? [] : [];

  return (
    <div style={{ minWidth: 0 }}>
      {/* Month nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <Button variant="ghost" onClick={() => shiftMonth(-1)} aria-label="Previous month">&larr;</Button>
        <div style={{ fontWeight: 600 }}>{monthLabel}</div>
        <Button variant="ghost" onClick={() => shiftMonth(1)} aria-label="Next month">&rarr;</Button>
      </div>

      {/* Weekday header */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
        {WEEKDAYS.map((w, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: '0.66rem', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>{w}</div>
        ))}
      </div>

      {/* Day grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {cells.map((iso, i) => {
          if (!iso) return <div key={`b${i}`} />;
          const count = byDate[iso]?.length ?? 0;
          const isToday = iso === today;
          const isSelected = iso === selected;
          const day = Number(iso.split('-')[2]);
          return (
            <button
              key={iso}
              onClick={() => setSelected(iso)}
              aria-pressed={isSelected}
              style={{
                aspectRatio: '1 / 1',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                borderRadius: 'var(--radius-sm)',
                border: `1px solid ${isSelected ? 'var(--gold)' : 'transparent'}`,
                background: isSelected ? 'var(--gold)' : count > 0 ? 'rgba(201,168,106,0.12)' : 'var(--card-bg)',
                color: isSelected ? '#1a1a1a' : isToday ? 'var(--gold)' : 'var(--text-primary)',
                fontWeight: isToday || count > 0 ? 700 : 400,
                cursor: 'pointer',
                fontSize: '0.82rem',
              }}
            >
              {day}
              <span style={{ height: 5, display: 'flex', gap: 2 }}>
                {count > 0 && (
                  <span style={{ width: 5, height: 5, borderRadius: 999, background: isSelected ? '#1a1a1a' : 'var(--gold)' }} />
                )}
                {count > 1 && (
                  <span style={{ width: 5, height: 5, borderRadius: 999, background: isSelected ? '#1a1a1a' : 'var(--gold)' }} />
                )}
              </span>
            </button>
          );
        })}
      </div>

      {/* Selected day's sessions */}
      <div style={{ marginTop: '1rem' }}>
        <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{selected ? fullDay(selected) : 'Select a day'}</div>
        {selectedSessions.length === 0 ? (
          <Muted>No sessions on this day.</Muted>
        ) : (
          <div style={{ display: 'grid', gap: '0.6rem' }}>
            {selectedSessions.map((a) => {
              // Started/past sessions are greyed out and can't be moved or cancelled.
              const past = hasStarted(a);
              return (
              <div key={a.id} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '0.6rem', padding: '0.7rem 0.85rem', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--radius-sm)', opacity: past ? 0.45 : 1 }}>
                <div style={{ flex: '1 1 10rem', minWidth: 0 }}>
                  <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {label12(a.start_time)} – {label12(a.end_time)}
                    {a.series_id ? (
                      <span style={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--gold)', border: '1px solid var(--gold-muted)', borderRadius: 999, padding: '0.05rem 0.4rem' }}>↻ Recurring</span>
                    ) : null}
                  </div>
                  <Muted>
                    {a.appointment_type ?? 'Training session'}
                    {a.trainer ? ` · ${a.trainer.first_name ?? ''} ${a.trainer.last_name ?? ''}` : ''}
                  </Muted>
                </div>
                {past ? (
                  <Muted style={{ flexShrink: 0 }}>Past</Muted>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    <Button variant="ghost" onClick={() => onReschedule(a)}>Reschedule</Button>
                    <Button variant="ghost" onClick={() => onCancel(a)} disabled={cancelPending}>Cancel</Button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
